/* Detail panel — 320px, tabbed, empty when nothing is selected (no placeholder noise). */
import { Radio, ShieldAlert, X } from 'lucide-react'
import { useDeck, useSelectedContact, useSelectedEntity, type DetailTab } from '../state/store'
import { affiliationLabel, affiliationToken } from '../lib/sidc'
import { approxMgrs, bearing, contactAge, formatLatLon, nm, thousands } from '../lib/format'
import { TracePanel } from './TracePanel'
import { DoctrineChainPanel } from './DoctrineChain'
import type { ComponentState } from '../data/types'

const TABS: { id: DetailTab; label: string }[] = [
  { id: 'status', label: 'Status' },
  { id: 'weapons', label: 'Weapons' },
  { id: 'sensors', label: 'Sensors' },
  { id: 'comms', label: 'Comms' },
  { id: 'doctrine', label: 'Doctrine' },
  { id: 'damage', label: 'Damage' },
  { id: 'trace', label: 'Trace' },
]

function Row({ label, value, unit }: { label: string; value: string; unit?: string }) {
  return (
    <div className="row">
      <span className="label" style={{ fontSize: 9.5 }}>
        {label}
      </span>
      <span className="num" style={{ fontSize: 12, color: 'var(--color-text-1)' }}>
        {value}
        {unit && <span className="unit">{unit}</span>}
      </span>
    </div>
  )
}

function healthToken(h: ComponentState['health']): string {
  return h === 'operational'
    ? 'var(--color-ok)'
    : h === 'degraded'
      ? 'var(--color-warn)'
      : 'var(--color-crit)'
}

function ComponentRow({ c }: { c: ComponentState }) {
  return (
    <div className="flex items-start gap-2 px-3 py-1.5" style={{ borderBottom: '1px solid var(--color-line-1)' }}>
      {/* Status carried by a labelled dot AND text — never colour alone */}
      <span
        className="mt-1.5 size-1.5 shrink-0 rounded-full"
        style={{ background: healthToken(c.health) }}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <div style={{ fontSize: 11.5, color: 'var(--color-text-1)' }}>{c.name}</div>
        {c.detail && (
          <div className="num" style={{ fontSize: 10, color: 'var(--color-text-3)' }}>
            {c.detail}
          </div>
        )}
      </div>
      <span
        className="label shrink-0"
        style={{ fontSize: 8.5, color: healthToken(c.health) }}
      >
        {c.health === 'operational' ? 'OP' : c.health === 'degraded' ? 'DEG' : 'DEST'}
      </span>
    </div>
  )
}

function FuelBar({ fraction }: { fraction: number }) {
  const pct = Math.round(fraction * 100)
  const token =
    fraction > 0.4 ? 'var(--color-ok)' : fraction > 0.2 ? 'var(--color-warn)' : 'var(--color-crit)'
  return (
    <div className="px-3 py-2" style={{ borderBottom: '1px solid var(--color-line-1)' }}>
      <div className="mb-1 flex items-center justify-between">
        <span className="label" style={{ fontSize: 9.5 }}>
          Fuel
        </span>
        <span className="num" style={{ fontSize: 12, color: token }}>
          {pct}
          <span className="unit">%</span>
        </span>
      </div>
      <div
        className="h-1 overflow-hidden rounded-full"
        style={{ background: 'var(--color-surface-3)' }}
        role="meter"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Fuel remaining"
      >
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: token }} />
      </div>
    </div>
  )
}

export function DetailPanel() {
  const selection = useDeck((s) => s.selection)
  const select = useDeck((s) => s.select)
  const tab = useDeck((s) => s.detailTab)
  const setTab = useDeck((s) => s.setDetailTab)
  const entity = useSelectedEntity()
  const contact = useSelectedContact()

  // Empty when nothing is selected — no placeholder noise
  if (!selection) {
    return (
      <aside
        className="flex w-80 shrink-0 flex-col items-center justify-center gap-2 border-l px-6"
        style={{ background: 'var(--color-surface-1)', borderColor: 'var(--color-line-2)' }}
      >
        <p style={{ fontSize: 12, color: 'var(--color-text-3)', textAlign: 'center' }}>
          Select a unit or track on the map.
        </p>
      </aside>
    )
  }

  const aff = entity?.affiliation ?? contact?.affiliation ?? '1'
  const title = entity?.designation ?? contact?.track ?? ''
  const subtitle = entity?.className ?? contact?.classification ?? ''

  return (
    <aside
      className="flex w-80 shrink-0 flex-col border-l"
      style={{ background: 'var(--color-surface-1)', borderColor: 'var(--color-line-2)' }}
      aria-label="Selection detail"
    >
      {/* Header */}
      <div
        className="flex items-start gap-2 px-3 py-2"
        style={{ borderBottom: '1px solid var(--color-line-2)' }}
      >
        <span
          className="mt-1 h-8 w-0.5 shrink-0 rounded-full"
          style={{ background: affiliationToken(aff as never) }}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <div className="num truncate" style={{ fontSize: 12.5, fontWeight: 600, letterSpacing: '.04em' }}>
            {title}
          </div>
          <div className="truncate" style={{ fontSize: 11, color: 'var(--color-text-2)' }}>
            {subtitle}
          </div>
          <div className="mt-0.5 flex items-center gap-1.5">
            <span
              className="label"
              style={{ fontSize: 8.5, color: affiliationToken(aff as never) }}
            >
              {affiliationLabel(aff as never)}
            </span>
            {contact && (
              <>
                <span style={{ fontSize: 9, color: 'var(--color-text-3)' }}>·</span>
                <span className="num" style={{ fontSize: 9.5, color: 'var(--color-text-3)' }}>
                  CONTACT
                </span>
              </>
            )}
          </div>
        </div>
        <button
          className="ctl"
          style={{ width: 22, height: 22, padding: 0 }}
          onClick={() => select(null)}
          aria-label="Clear selection"
          title="Clear selection  (Esc)"
        >
          <X size={13} />
        </button>
      </div>

      {/* Contact: the picture is an estimate, and the panel says so */}
      {contact && (
        <div className="flex-1 overflow-y-auto">
          <div
            className="mx-2 mt-2 flex items-start gap-2 rounded px-2 py-1.5"
            style={{ background: 'rgba(229,192,75,0.09)', border: '1px solid rgba(229,192,75,0.22)' }}
          >
            <ShieldAlert size={12} style={{ color: 'var(--color-unknown)', marginTop: 1 }} aria-hidden />
            <p style={{ fontSize: 10.5, color: 'var(--color-text-2)', lineHeight: 1.4 }}>
              Sensor-derived estimate, not truth. Position uncertainty{' '}
              <span className="num">±{nm(contact.uncertaintyNm)} nm</span>.
            </p>
          </div>

          <div className="mt-2">
            <Row label="Classification" value={contact.classification} />
            <Row label="Confidence" value={String(contact.confidence)} unit="%" />
            <Row label="Position" value={formatLatLon(contact.lat, contact.lon)} />
            <Row label="MGRS" value={approxMgrs(contact.lat, contact.lon)} />
            {contact.courseDeg !== undefined && (
              <Row label="Course" value={bearing(contact.courseDeg)} />
            )}
            {contact.speedKt !== undefined && (
              <Row label="Speed" value={String(contact.speedKt)} unit="kt" />
            )}
            <Row label="Uncertainty" value={`±${nm(contact.uncertaintyNm)}`} unit="nm" />
            <Row label="Age" value={contactAge(contact.ageSec).text} />
            <Row label="Detected by" value={contact.detectedBy} />
            <Row label="Emitting" value={contact.emitting ? 'YES' : 'NO'} />
          </div>

          <div className="p-2">
            <button
              className="ctl w-full"
              style={{ background: 'var(--color-surface-3)', fontSize: 11.5, minHeight: 28 }}
            >
              <Radio size={12} />
              Why can&apos;t I see more detail?
            </button>
          </div>
        </div>
      )}

      {/* Entity */}
      {entity && (
        <>
          {/* Seven tabs do not fit 320px in one row, and horizontal scroll HIDES tabs —
              an operator cannot discover what they cannot see. Two rows of four keeps
              every section visible at all times. */}
          <div
            className="grid shrink-0 grid-cols-4"
            role="tablist"
            aria-label="Detail sections"
            style={{ borderBottom: '1px solid var(--color-line-2)' }}
          >
            {TABS.map((t) => {
              const active = tab === t.id
              return (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setTab(t.id)}
                  className="transition-colors"
                  style={{
                    fontSize: 10.5,
                    minHeight: 26,
                    fontWeight: active ? 500 : 400,
                    color: active ? 'var(--color-text-1)' : 'var(--color-text-3)',
                    background: active ? 'var(--color-accent-dim)' : undefined,
                    borderBottom: `2px solid ${active ? 'var(--color-accent)' : 'transparent'}`,
                    borderRight: '1px solid var(--color-line-1)',
                  }}
                >
                  {t.label}
                </button>
              )
            })}
          </div>

          <div className="flex-1 overflow-y-auto">
            {tab === 'status' && (
              <div>
                <Row label="Position" value={formatLatLon(entity.lat, entity.lon)} />
                <Row label="MGRS" value={approxMgrs(entity.lat, entity.lon)} />
                <Row label="Course" value={bearing(entity.courseDeg)} />
                <Row label="Speed" value={String(entity.speedKt)} unit="kt" />
                {entity.altitudeFt !== undefined && (
                  <Row label="Altitude" value={thousands(entity.altitudeFt)} unit="ft" />
                )}
                {entity.depthM !== undefined && (
                  <Row label="Depth" value={String(entity.depthM)} unit="m" />
                )}
                <FuelBar fraction={entity.fuelFraction} />
                <Row label="Readiness" value={entity.readiness} />
                <Row label="Mission" value={entity.mission ?? '—'} />
                <Row label="EMCON radar" value={entity.emcon.radar.toUpperCase()} />
                <Row label="EMCON comms" value={entity.emcon.comms.toUpperCase()} />
              </div>
            )}

            {(tab === 'weapons' || tab === 'sensors' || tab === 'comms' || tab === 'damage') && (
              <div>
                {entity.components
                  .filter((c) => {
                    if (tab === 'weapons') return ['mount', 'magazine', 'illuminator'].includes(c.kind)
                    if (tab === 'sensors') return c.kind === 'sensor'
                    if (tab === 'comms') return c.kind === 'comm'
                    return true
                  })
                  .map((c) => (
                    <ComponentRow key={c.id} c={c} />
                  ))}
              </div>
            )}

            {tab === 'doctrine' && <DoctrineChainPanel entityId={entity.id} />}
            {tab === 'trace' && <TracePanel entityId={entity.id} />}
          </div>
        </>
      )}
    </aside>
  )
}
